import { immerable } from 'immer'
import { useState, useRef } from 'react'
import api from '@/services/api/transaction.js'
import titleCase from '@/utils/title-case'
import KeyHelper from '@/utils/key-helper'

class Transaction {

	[immerable] = true

	constructor(params) {
		this.created = params.created
		this.modified = params.modified
		this.dataElement = params.dataElement
		this.status = params.status
		this.system = params.system
		this.onBehalfOf = params.onBehalfOf
		this.on_behalf_of_id = params.on_behalf_of_id
		this.on_behalf_of_name = params.on_behalf_of_name
		this.type = params.type

		this.id = params.id
		this.attachments = params.attachments
		this.verificationStatus = params.verificationStatus
		this.issuer = params.issuer
		this.referenceId = params.referenceId
		this.sysId = params.sysId
		this.orgId = params.orgId
		this.storedFor = params.storedFor
		this.isShared = params.isShared
		this.refMessageId = params.refMessageId
		this.isTTFlow = params?.isTTFlow

		this.payload = params.payload
		this.verificationStatus = params.verificationStatus
		this.receivingType = params.receivingType
		this.providerTransactionStatus = params?.providerTransactionStatus
	}
}

const useTransaction = () => {

	const [itemsData, setItems] = useState([])
	const [metaData, setMeta] = useState(null)
	const [currentItem, setCurrentItem] = useState(null)
	const [loading, setLoading] = useState(false)
	const [errorMsg, setError] = useState()
	const [activityLog, setActivityLog] = useState([]);
	const [packageErrorMsg, setPackageErrorMsg] = useState("");

	const lastRequestPayloadKeyRef = useRef(null)

	async function fetch(option) {

		setLoading(true)
		const [error, response] = await api.index(option)

		setLoading(false)
		if (error) {
			return setError(error)
		}
		const { items, meta } = response

		if (!Array.isArray(items)) {

			return setError('Transaction is not an array')

		}

		const serializedData = items.map(entry => serialize(entry))

		const newItems = serializedData.map(params => new Transaction(params))

		setMeta(meta)

		setItems(newItems)

	}

	async function fetchOne(id) {
		setLoading(true)

		const [error, response] = await api.findOne(id)

		setLoading(false)

		if (error) {
			return setError(error)
		}

		const serializedData = serialize(response)
		const newItems = new Transaction(serializedData)


		setCurrentItem(newItems)
	}

	async function postFetch(option) {

		const currentPayloadKey = KeyHelper.createKeyOfObject(option)

		if(KeyHelper.isSameKey(lastRequestPayloadKeyRef.current, currentPayloadKey)) return;

		lastRequestPayloadKeyRef.current = currentPayloadKey

		setLoading(true)

		const [error, response] = await api.getMessages(option)

		setLoading(false)

		if (error) {
			return setError(error)
		}

		const { items } = response

		if (!Array.isArray(items)) {
			return setError('Transaction is not an array')
		}

		const serializedData = items.map(entry => serialize(entry))
		const newItems = serializedData.map(params => new Transaction(params))

		setItems(newItems)
	}
	

	async function getActivityLog(id) {
		const [error, response] = await api.activityLog(id);

		if (error) {
			setActivityLog([]);
			return setPackageErrorMsg("Transaction API has error");
		}

		setActivityLog(response);
	}

	async function getActivityLogByTransactionRequestId(id) {
		const [error, response] = await api.activityLogByTransactionRequestId(id);

		if (error) {
			setActivityLog([]);
			return setPackageErrorMsg("Transaction API has error");
		}

		setActivityLog(response);
	}

	function serialize(entry) {
		return {
			id: entry.id,
			created: entry.created,
			modified: entry.modified,
			system: {
				id: entry.orgId,
			},
			status: titleCase(entry.messageStatus),
			dataElement: {
				name: entry.dataElement.elementName,
				id: entry.dataElement.dataElementId,
				elementSchema: entry.dataElement.elementSchema
			},
			on_behalf_of_id: entry.onBehalfOf && entry.onBehalfOf[0] ? entry.onBehalfOf[0].id : null,
			on_behalf_of_name: entry.onBehalfOf && entry.onBehalfOf[0] ? entry.onBehalfOf[0].name : '',
			type: entry.messageType,
			attachments: entry.attachments ? entry.attachments.filter(attachment => attachment) : undefined,
			verificationStatus: entry.verificationStatus,
			issuer: entry.issuerDetail && entry.issuerDetail[0] ? entry.issuerDetail[0].name : null,
			referenceId: entry.referenceId,
			sysId: entry.systemId,
			orgId: entry.orgId,
			storedFor: entry.storedFor,
			isShared: entry.isSharingEnabled,
			refMessageId: entry.refMessageId,
			isTTFlow: entry?.isTTFlow,
			elementSchema: entry?.dataElement.elementSchema,
			payload: entry.payload,
			receivingType: entry.receivingType,
			providerTransactionStatus: entry?.providerTransactionStatus,
		}
	}

	function view(id) {
		const newCurrentItem = itemsData.find(item => item.id === id)
		setCurrentItem(newCurrentItem)
	}

	return {
		currentItem,
		error:errorMsg,
		fetch,
		items:itemsData,
		loading,
		meta:metaData,
		setCurrentItem,
		setItems,
		view,
		postFetch,
		fetchOne,
		activityLog,
		getActivityLog,
		getActivityLogByTransactionRequestId,
		packageErrorMsg,
		setPackageErrorMsg
	}

}

export default useTransaction
